import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  Users, Building2, IndianRupee, Zap, BarChart3, Shield, CheckCircle2,
  ArrowRight, Phone, Mail, MapPin, Star, Sparkles, LayoutGrid, FileSpreadsheet,
  Smartphone, Globe, Clock, ShieldCheck, X, Check, Loader2, ReceiptIndianRupee,
  TrendingUp, BedDouble
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_d23d8491-b3b1-4bd6-8f0e-4d1750125554/artifacts/uxopwla6_WhatsApp%20Image%202026-03-10%20at%204.20.57%20PM.jpeg";
const API = process.env.REACT_APP_BACKEND_URL;

const HEAD = "font-['Cabinet_Grotesk',_'Manrope',_sans-serif]";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

/* ---------------------------------- Header --------------------------------- */
const Header = () => (
  <nav
    data-testid="landing-nav"
    className="fixed top-0 left-0 right-0 z-50 bg-white/70 backdrop-blur-xl border-b border-white/40 shadow-[0_2px_20px_rgb(0,0,0,0.03)]"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <div className="flex items-center justify-between h-[72px]">
        <Link to="/" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="flex items-center gap-3 group [perspective:600px]" data-testid="landing-logo-link" title="Go to home">
          <img src={LOGO_URL} alt="StayPoint" className="w-10 h-10 rounded-xl object-contain ring-1 ring-zinc-200 shadow-md transition-transform duration-300 group-hover:[transform:rotateY(22deg)_scale(1.08)] group-hover:shadow-xl" />
          <div className="leading-tight">
            <h1 className={`${HEAD} text-[19px] font-extrabold text-zinc-950 tracking-tight group-hover:text-indigo-700 transition-colors`}>StayPoint</h1>
            <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-400 font-semibold">PG Management</p>
          </div>
        </Link>
        <div className="hidden lg:flex items-center gap-9">
          {['Features', 'Why Us', 'How it Works', 'FAQ'].map((l) => (
            <a key={l} href={`#${l.toLowerCase().replace(/ /g, '-')}`} className="text-sm font-medium text-zinc-600 hover:text-[#002FA7] transition-colors">{l}</a>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <Link to="/login">
            <Button variant="ghost" data-testid="nav-login-btn" className="text-zinc-700 hover:text-[#002FA7] hover:bg-zinc-100 font-medium">Login</Button>
          </Link>
          <a href="#contact">
            <Button data-testid="nav-demo-btn" className="bg-[#002FA7] hover:bg-[#00227A] text-white rounded-xl font-semibold shadow-[0_8px_24px_-6px_rgba(0,47,167,0.5)] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_14px_30px_-6px_rgba(0,47,167,0.6)] active:translate-y-0 active:shadow-[0_4px_12px_-4px_rgba(0,47,167,0.5)]">
              Book a Demo
            </Button>
          </a>
        </div>
      </div>
    </div>
  </nav>
);

/* ----------------------------- 3D Dashboard mock --------------------------- */
const DashboardMock = () => (
  <div className="relative" style={{ perspective: '1600px' }}>
    {/* glow */}
    <div className="absolute -inset-10 bg-[radial-gradient(circle_at_70%_30%,rgba(0,47,167,0.18),transparent_60%)] blur-2xl" />
    <motion.div
      initial={{ opacity: 0, rotateX: 18, rotateY: -22, y: 40 }}
      animate={{ opacity: 1, rotateX: 12, rotateY: -16, y: 0 }}
      transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ rotateX: 6, rotateY: -8, scale: 1.02 }}
      style={{ transformStyle: 'preserve-3d' }}
      className="relative rounded-2xl bg-white border border-zinc-200 shadow-[0_40px_80px_-20px_rgba(0,47,167,0.35)] p-5 w-full"
    >
      <div className="flex items-center justify-between mb-5">
        <div>
          <p className="text-[11px] uppercase tracking-wider text-zinc-400 font-semibold">Total Collection</p>
          <p className={`${HEAD} text-2xl font-extrabold text-zinc-950`}>₹ 8,42,500</p>
        </div>
        <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full">
          <TrendingUp className="w-3.5 h-3.5" /> +18%
        </span>
      </div>
      <div className="grid grid-cols-3 gap-3 mb-5">
        {[['Occupied', '92%', 'text-[#002FA7]'], ['Dues', '₹46k', 'text-rose-500'], ['Guests', '184', 'text-emerald-600']].map(([k, v, c]) => (
          <div key={k} className="rounded-xl bg-zinc-50 border border-zinc-100 p-3">
            <p className="text-[10px] uppercase tracking-wide text-zinc-400 font-semibold">{k}</p>
            <p className={`text-lg font-bold ${c}`}>{v}</p>
          </div>
        ))}
      </div>
      <div className="flex items-end gap-2 h-24 px-1">
        {[40, 65, 50, 80, 60, 95, 72].map((h, i) => (
          <motion.div
            key={i}
            initial={{ height: 0 }}
            animate={{ height: `${h}%` }}
            transition={{ delay: 0.6 + i * 0.07, duration: 0.5 }}
            className="flex-1 rounded-t-md bg-gradient-to-t from-[#002FA7] to-[#5B8DEF]"
          />
        ))}
      </div>
    </motion.div>

    {/* floating receipt card */}
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: [0, -10, 0] }}
      transition={{ y: { repeat: Infinity, duration: 4, ease: 'easeInOut' }, opacity: { duration: 0.8, delay: 0.8 } }}
      className="absolute -left-6 bottom-6 bg-white rounded-xl border border-zinc-100 shadow-2xl p-3.5 flex items-center gap-3"
    >
      <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center">
        <CheckCircle2 className="w-5 h-5 text-emerald-600" />
      </div>
      <div>
        <p className="text-xs font-semibold text-zinc-900">Rent Received</p>
        <p className="text-[10px] text-zinc-400">Receipt #1042 · auto-logged</p>
      </div>
    </motion.div>

    {/* floating reminder pill */}
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: [0, 10, 0] }}
      transition={{ y: { repeat: Infinity, duration: 5, ease: 'easeInOut' }, opacity: { duration: 0.8, delay: 1 } }}
      className="absolute -right-4 top-8 bg-[#002FA7] text-white rounded-xl shadow-2xl px-4 py-2.5"
    >
      <p className="text-[10px] uppercase tracking-wide opacity-70 font-semibold">Due Today</p>
      <p className="text-sm font-bold">6 reminders sent</p>
    </motion.div>
  </div>
);

/* ----------------------------------- Hero ---------------------------------- */
const Hero = () => {
  return (
  <section className="relative pt-36 pb-24 px-6 md:px-10 overflow-hidden bg-[#FAFAFA]">
    {/* gradient mesh bg */}
    <div className="absolute inset-0 -z-10">
      <div className="absolute top-0 right-0 w-[60%] h-[70%] bg-[radial-gradient(ellipse_at_top_right,rgba(0,47,167,0.10),transparent_60%)]" />
      <div className="absolute bottom-0 left-0 w-[50%] h-[50%] bg-[radial-gradient(ellipse_at_bottom_left,rgba(16,185,129,0.08),transparent_60%)]" />
      <div className="absolute inset-0 opacity-[0.4] [background-image:linear-gradient(to_right,#e9e9ee_1px,transparent_1px),linear-gradient(to_bottom,#e9e9ee_1px,transparent_1px)] [background-size:64px_64px] [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)]" />
    </div>

    <div className="max-w-7xl mx-auto grid lg:grid-cols-[1.05fr_1fr] gap-16 items-center">
      <div>
        <motion.span
          variants={fadeUp} initial="hidden" animate="show"
          className="inline-flex items-center gap-2 rounded-full border border-[#002FA7]/20 bg-[#002FA7]/5 px-3.5 py-1.5 text-xs font-semibold text-[#002FA7] mb-6"
        >
          <Sparkles className="w-3.5 h-3.5" /> Built by Property Owners · Trusted by Property Managers
        </motion.span>
        <motion.h1
          variants={fadeUp} initial="hidden" animate="show" custom={1}
          className={`${HEAD} text-5xl sm:text-6xl lg:text-[60px] leading-[1.02] font-black tracking-tighter text-zinc-950 mb-6`}
        >
          Manage Your PG, Hostel &amp; Rental Properties <span className="relative whitespace-nowrap text-[#002FA7]">Effortlessly
            <svg className="absolute -bottom-2 left-0 w-full" height="10" viewBox="0 0 300 10" fill="none"><path d="M2 7C75 2 225 2 298 7" stroke="#10B981" strokeWidth="3.5" strokeLinecap="round"/></svg>
          </span>
        </motion.h1>
        <motion.p
          variants={fadeUp} initial="hidden" animate="show" custom={2}
          className="text-lg sm:text-xl text-zinc-600 leading-relaxed mb-9 max-w-xl"
        >
          Complete Property Management Software for <span className="font-semibold text-zinc-900">Occupancy, Rent Collection, Guest Management, Accounting and Operations</span> — across PG, Hostel and Co-Living spaces.
        </motion.p>
        <motion.div variants={fadeUp} initial="hidden" animate="show" custom={3} className="flex flex-col sm:flex-row gap-4">
          <a href="#contact">
            <Button data-testid="hero-book-demo-btn" size="lg" className="bg-[#002FA7] hover:bg-[#00227A] text-white text-base rounded-xl px-7 h-13 py-3.5 font-semibold shadow-[0_14px_30px_-8px_rgba(0,47,167,0.55)] group transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_22px_44px_-10px_rgba(0,47,167,0.65)] active:translate-y-0 active:shadow-[0_8px_18px_-6px_rgba(0,47,167,0.5)]">
              Book Demo <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </a>
          <Link to="/request-access">
            <Button data-testid="hero-request-access-btn" size="lg" variant="outline" className="text-base rounded-xl px-7 h-13 py-3.5 border-zinc-300 text-zinc-800 hover:bg-white hover:border-[#002FA7] font-semibold transition-all duration-200 hover:-translate-y-1 hover:shadow-lg active:translate-y-0">
              Request Access
            </Button>
          </Link>
        </motion.div>
        <motion.p variants={fadeUp} initial="hidden" animate="show" custom={4} className="text-sm text-zinc-500 mt-6 max-w-lg">
          Our own PGs &amp; hostels run on StayPoint — proven in real-world operations, every single day.
        </motion.p>
        <motion.div variants={fadeUp} initial="hidden" animate="show" custom={5} className="flex flex-wrap items-center gap-x-7 gap-y-2 mt-5 text-sm text-zinc-500">
          {['No spreadsheets', 'Setup in 24 hours', 'Excel exports'].map((t) => (
            <span key={t} className="inline-flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> {t}</span>
          ))}
        </motion.div>
      </div>
      <DashboardMock />
    </div>
  </section>
  );
};

/* ------------------------------- Stats strip ------------------------------- */
const StatsStrip = () => {
  const stats = [
    [ShieldCheck, '100%', 'Accurate Calculations'],
    [Building2, 'Multi', 'Property & Room Control'],
    [FileSpreadsheet, 'Excel', 'Reports & Exports'],
    [Users, 'Role-based', 'Access Control'],
  ];
  return (
    <section className="border-y border-zinc-200 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-2 lg:grid-cols-4 divide-x divide-zinc-100">
        {stats.map(([Icon, big, small], i) => (
          <motion.div key={small} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
            className="flex items-center gap-4 px-5 py-7">
            <div className="w-11 h-11 rounded-xl bg-[#002FA7]/8 flex items-center justify-center shrink-0">
              <Icon className="w-5 h-5 text-[#002FA7]" />
            </div>
            <div>
              <p className={`${HEAD} text-xl font-extrabold text-zinc-950 leading-none`}>{big}</p>
              <p className="text-xs text-zinc-500 mt-1">{small}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

/* ----------------------------- Feature showcase ---------------------------- */
const FloorPlanMock = () => (
  <div className="grid grid-cols-3 gap-2.5">
    {[['101', 'occ'], ['102', 'occ'], ['103', 'vac'], ['104', 'notice'], ['105', 'occ'], ['106', 'vac']].map(([n, s]) => (
      <div key={n} className={`rounded-xl p-3 border text-center ${s === 'occ' ? 'bg-[#002FA7] text-white border-[#002FA7]' : s === 'vac' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
        <BedDouble className="w-4 h-4 mx-auto mb-1 opacity-80" />
        <p className="text-xs font-bold">{n}</p>
        <p className="text-[9px] uppercase tracking-wide opacity-70">{s === 'occ' ? 'Filled' : s === 'vac' ? 'Vacant' : 'Notice'}</p>
      </div>
    ))}
  </div>
);

const LedgerMock = () => (
  <div className="space-y-2">
    {[['Rent', '₹12,000', 'text-emerald-600'], ['Electricity', '₹1,240', 'text-emerald-600'], ['Outstanding', '₹3,500', 'text-rose-500'], ['Deposit', '₹15,000', 'text-zinc-700']].map(([k, v, c]) => (
      <div key={k} className="flex items-center justify-between rounded-lg bg-zinc-50 border border-zinc-100 px-4 py-2.5">
        <span className="text-sm text-zinc-600">{k}</span>
        <span className={`text-sm font-bold ${c}`}>{v}</span>
      </div>
    ))}
  </div>
);

const RentMock = () => (
  <div className="space-y-2.5">
    {[['Aman S.', 'Paid', true], ['Priya R.', 'Paid', true], ['Rahul K.', 'Reminder sent', false]].map(([n, s, ok]) => (
      <div key={n} className="flex items-center justify-between rounded-lg bg-white border border-zinc-100 px-4 py-2.5 shadow-sm">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-full bg-[#002FA7]/10 text-[#002FA7] text-xs font-bold flex items-center justify-center">{n[0]}</div>
          <span className="text-sm text-zinc-700">{n}</span>
        </div>
        <span className={`text-xs font-semibold inline-flex items-center gap-1 ${ok ? 'text-emerald-600' : 'text-amber-600'}`}>
          {ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />} {s}
        </span>
      </div>
    ))}
  </div>
);

const FeatureShowcase = () => {
  const features = [
    {
      icon: Users, tag: 'Guest Lifecycle',
      title: 'Every guest, from check-in to check-out',
      desc: 'Onboarding, advance booking, notice period, overstay and leave settlement — fully tracked with an emergency/parent contact on file.',
      points: ['Notice & overstay auto-tracking', 'Room shift with pro-rated rent', 'Complete guest statement & history'],
      visual: 'image',
    },
    {
      icon: LayoutGrid, tag: 'Floor Plan',
      title: 'Your whole property on one screen',
      desc: 'See filled, vacant and under-notice beds at a glance across every property, floor and sharing type — and allot a bed in one click.',
      points: ['Real-time occupancy view', 'Single / double / triple sharing', 'One-click bed allotment'],
      visual: 'floor',
    },
    {
      icon: IndianRupee, tag: 'Rent & Dues',
      title: 'Collect rent without the chasing',
      desc: 'Automated monthly rent and dues, deposit tracking and instant receipt generation — so nothing slips through the cracks.',
      points: ['Auto rent & deposit tracking', 'Instant payment receipts', 'Outstanding dues in real time'],
      visual: 'rent',
    },
    {
      icon: BarChart3, tag: 'Digital Accountant',
      title: '24/7 accounting at your fingertips',
      desc: 'Daily Hisab, Outstanding, Ledger and category-wise reports — all exportable to Excel and reconciled to the rupee.',
      points: ['Daily Hisab & Ledger', 'Category & month filters', 'Excel export for everything'],
      visual: 'ledger',
    },
  ];

  return (
    <section id="features" className="py-28 px-6 md:px-10 bg-[#FAFAFA]">
      <div className="max-w-7xl mx-auto">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="max-w-2xl mb-20">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#002FA7] mb-3">Everything you need</p>
          <h2 className={`${HEAD} text-4xl sm:text-5xl font-bold tracking-tight text-zinc-950 mb-4`}>One dashboard to run the entire PG</h2>
          <p className="text-lg text-zinc-600">From guest onboarding to financial reporting — StayPoint handles it all, accurately.</p>
        </motion.div>

        <div className="space-y-24">
          {features.map((f, i) => {
            const reverse = i % 2 === 1;
            return (
              <div key={f.tag} className={`grid lg:grid-cols-2 gap-12 lg:gap-16 items-center`}>
                <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className={reverse ? 'lg:order-2' : ''}>
                  <span className="inline-flex items-center gap-2 rounded-full bg-white border border-zinc-200 px-3 py-1 text-xs font-semibold text-[#002FA7] mb-5">
                    <f.icon className="w-3.5 h-3.5" /> {f.tag}
                  </span>
                  <h3 className={`${HEAD} text-3xl sm:text-4xl font-bold tracking-tight text-zinc-950 mb-4`}>{f.title}</h3>
                  <p className="text-lg text-zinc-600 mb-6 leading-relaxed">{f.desc}</p>
                  <ul className="space-y-3">
                    {f.points.map((p) => (
                      <li key={p} className="flex items-center gap-3 text-zinc-800">
                        <span className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center shrink-0"><Check className="w-3 h-3 text-emerald-600" /></span>
                        <span className="font-medium">{p}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
                  whileHover={{ rotateX: 4, rotateY: reverse ? -5 : 5, scale: 1.015 }}
                  style={{ transformStyle: 'preserve-3d', perspective: 1000 }}
                  className={`${reverse ? 'lg:order-1' : ''} relative rounded-3xl bg-white border border-zinc-200 shadow-[0_30px_60px_-25px_rgba(0,47,167,0.25)] p-6`}
                >
                  <div className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-[#002FA7]/40 to-transparent" />
                  {f.visual === 'image' && (
                    <div className="relative rounded-2xl overflow-hidden w-full h-64">
                      <img src="https://customer-assets.emergentagent.com/job_111d5bf7-7334-4684-b610-4826247c45bb/artifacts/dag0t6l8_Building%20Stay%20Point.jpeg" alt="Stay Point building" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/55 via-transparent to-transparent" />
                      {/* building nameplate */}
                      <div className="absolute top-4 left-4 flex items-center gap-2 rounded-xl bg-white/90 backdrop-blur-sm px-3 py-2 shadow-lg">
                        <img src={LOGO_URL} alt="StayPoint" className="w-6 h-6 rounded-md object-contain" />
                        <span className={`${HEAD} text-sm font-extrabold tracking-tight text-zinc-950`}>Stay Point</span>
                      </div>
                      <p className="absolute bottom-3 left-4 text-white text-xs font-medium drop-shadow">Our own property, powered by StayPoint</p>
                    </div>
                  )}
                  {f.visual === 'floor' && <FloorPlanMock />}
                  {f.visual === 'rent' && <RentMock />}
                  {f.visual === 'ledger' && <LedgerMock />}
                </motion.div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ------------------------------- Comparison -------------------------------- */
const Comparison = () => {
  const rows = [
    ['Rent & dues tracking', 'Manual, error-prone formulas', 'Automatic & 100% accurate'],
    ['Outstanding dues', 'Recalculated by hand', 'Real-time, always current'],
    ['Receipts & reports', 'Copy-paste every month', 'One-click Excel export'],
    ['Multiple staff', 'Shared file, no control', 'Role-based access'],
    ['Notice & overstay', 'Easy to miss', 'Auto-tracked & pro-rated'],
  ];
  return (
    <section id="why-us" className="py-28 px-6 md:px-10 bg-white">
      <div className="max-w-6xl mx-auto">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="text-center max-w-2xl mx-auto mb-16">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#002FA7] mb-3">Why switch</p>
          <h2 className={`${HEAD} text-4xl sm:text-5xl font-bold tracking-tight text-zinc-950 mb-4`}>Why rent like it's 1999?</h2>
          <p className="text-lg text-zinc-600">Excel and registers can't keep up. StayPoint does the heavy lifting for you.</p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6">
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="rounded-3xl border border-zinc-200 bg-zinc-50 p-8">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-10 h-10 rounded-xl bg-zinc-200 flex items-center justify-center"><FileSpreadsheet className="w-5 h-5 text-zinc-500" /></span>
              <h3 className={`${HEAD} text-xl font-bold text-zinc-500`}>Excel & Registers</h3>
            </div>
            <ul className="space-y-4">
              {rows.map((r) => (
                <li key={r[0]} className="flex items-start gap-3 text-zinc-500">
                  <X className="w-5 h-5 text-rose-400 shrink-0 mt-0.5" />
                  <span>{r[1]}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={1}
            whileHover={{ y: -6 }}
            className="rounded-3xl border border-[#002FA7]/20 bg-[#002FA7] text-white p-8 shadow-[0_40px_80px_-30px_rgba(0,47,167,0.6)] relative overflow-hidden"
          >
            <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-white/10 blur-2xl" />
            <div className="flex items-center gap-3 mb-6">
              <span className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center"><Sparkles className="w-5 h-5 text-white" /></span>
              <h3 className={`${HEAD} text-xl font-bold`}>StayPoint Smart</h3>
            </div>
            <ul className="space-y-4">
              {rows.map((r) => (
                <li key={r[0]} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-300 shrink-0 mt-0.5" />
                  <span className="font-medium">{r[2]}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

/* ------------------------------ How it works ------------------------------- */
const HowItWorks = () => {
  const steps = [
    ['Request Access', 'Tell us about your PG — name, city and capacity.'],
    ['Setup in 24 hours', 'Our team configures your account and onboards you.'],
    ['Add properties & rooms', 'Set up floors, rooms, beds and sharing types.'],
    ['Start collecting', 'Track rent, dues and reports — all in one place.'],
  ];
  return (
    <section id="how-it-works" className="py-28 px-6 md:px-10 bg-[#FAFAFA]">
      <div className="max-w-7xl mx-auto">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="text-center max-w-2xl mx-auto mb-16">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#002FA7] mb-3">Get started fast</p>
          <h2 className={`${HEAD} text-4xl sm:text-5xl font-bold tracking-tight text-zinc-950`}>Up and running in 4 simple steps</h2>
        </motion.div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((s, i) => (
            <motion.div key={s[0]} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
              className="relative rounded-2xl bg-white border border-zinc-200 p-7 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-1 transition-transform">
              <div className={`${HEAD} text-5xl font-black text-[#002FA7]/10 absolute top-4 right-5`}>{i + 1}</div>
              <div className="w-11 h-11 rounded-xl bg-[#002FA7] text-white flex items-center justify-center font-bold mb-5">{i + 1}</div>
              <h3 className={`${HEAD} text-lg font-bold text-zinc-950 mb-2`}>{s[0]}</h3>
              <p className="text-sm text-zinc-600 leading-relaxed">{s[1]}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ------------------------------ Testimonials ------------------------------- */
const Testimonials = () => {
  const data = [
    ['StayMore Homes', 'Co-Living', 'Rent collection used to take days. Now everything is on one screen and our dues dropped sharply.'],
    ['Deera Homes', 'Hostel', 'The accuracy of the financial reports is exactly what we needed. No more Excel mistakes.'],
    ['Kripal Homes', 'PG for Students', 'Floor plan view tells me instantly which beds are vacant. Allotment is a one-click job now.'],
    ['Dev PG', 'Boys PG', 'Notice and overstay tracking is automatic. We never lose track of who is leaving when.'],
    ['Capital PG', 'Working Professionals', 'Daily Hisab and ledger reports save our accountant hours every single week.'],
    ['Royal Homes', 'Co-Living', 'Multi-user access means my managers handle their properties without seeing everything.'],
  ];
  const loop = [...data, ...data];
  return (
    <section className="py-28 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-10 mb-14">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="text-center max-w-2xl mx-auto">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#002FA7] mb-3">Loved by owners</p>
          <h2 className={`${HEAD} text-4xl sm:text-5xl font-bold tracking-tight text-zinc-950`}>PG owners who trust StayPoint</h2>
        </motion.div>
      </div>
      <div className="relative">
        <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-white to-transparent z-10" />
        <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-white to-transparent z-10" />
        <motion.div
          className="flex gap-6 w-max px-6"
          animate={{ x: ['0%', '-50%'] }}
          transition={{ repeat: Infinity, duration: 40, ease: 'linear' }}
        >
          {loop.map((t, i) => (
            <div key={i} className="w-[360px] shrink-0 rounded-2xl border border-zinc-200 bg-[#FAFAFA] p-7 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <div className="flex gap-0.5 mb-4">
                {[...Array(5)].map((_, s) => <Star key={s} className="w-4 h-4 fill-amber-400 text-amber-400" />)}
              </div>
              <p className="text-zinc-700 leading-relaxed mb-6">"{t[2]}"</p>
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-[#002FA7] text-white flex items-center justify-center font-bold">{t[0][0]}</div>
                <div>
                  <p className={`${HEAD} font-bold text-zinc-950`}>{t[0]}</p>
                  <p className="text-xs text-zinc-500">{t[1]}</p>
                </div>
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

/* ------------------------------- App coming -------------------------------- */
const AppSoon = () => (
  <section className="py-24 px-6 md:px-10 bg-[#FAFAFA]">
    <div className="max-w-6xl mx-auto rounded-[32px] bg-zinc-950 text-white p-10 md:p-16 relative overflow-hidden">
      <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-[#002FA7]/40 blur-3xl" />
      <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-emerald-500/20 blur-3xl" />
      <div className="relative grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3.5 py-1.5 text-xs font-semibold mb-6">
            <Globe className="w-3.5 h-3.5" /> Web app available now
          </span>
          <h2 className={`${HEAD} text-4xl sm:text-5xl font-bold tracking-tight mb-5`}>Manage from any device, anywhere</h2>
          <p className="text-lg text-zinc-300 mb-8 max-w-lg">StayPoint runs right in your browser — nothing to install. And our dedicated <span className="text-white font-semibold">mobile app is coming soon</span>.</p>
          <div className="flex flex-wrap gap-4">
            <Link to="/request-access">
              <Button data-testid="appsoon-start-btn" size="lg" className="bg-white text-zinc-950 hover:bg-zinc-200 rounded-xl font-semibold">
                <Globe className="w-4 h-4 mr-2" /> Open Web App
              </Button>
            </Link>
            <Button disabled size="lg" variant="outline" className="border-white/30 text-white/80 bg-white/5 rounded-xl font-semibold cursor-default">
              <Smartphone className="w-4 h-4 mr-2" /> Mobile App — Coming Soon
            </Button>
          </div>
        </div>
        <div className="flex justify-center lg:justify-end">
          <motion.div
            animate={{ y: [0, -12, 0] }} transition={{ repeat: Infinity, duration: 5, ease: 'easeInOut' }}
            className="w-52 h-[420px] rounded-[36px] border-[6px] border-zinc-800 bg-zinc-900 shadow-2xl p-3"
          >
            <div className="h-full w-full rounded-[24px] bg-gradient-to-b from-[#002FA7] to-[#001b63] p-4 flex flex-col">
              <div className="flex justify-between items-center text-[10px] text-white/60 mb-6">
                <span>StayPoint</span><span>9:41</span>
              </div>
              <p className="text-white/60 text-[10px] uppercase tracking-wide">Collection</p>
              <p className={`${HEAD} text-2xl font-extrabold mb-5`}>₹ 8.4L</p>
              <div className="space-y-2.5">
                {['Rent received', 'Dues pending', 'New guest'].map((t) => (
                  <div key={t} className="rounded-xl bg-white/10 px-3 py-2.5 text-xs text-white/90 flex items-center gap-2">
                    <ReceiptIndianRupee className="w-3.5 h-3.5" /> {t}
                  </div>
                ))}
              </div>
              <div className="mt-auto rounded-xl bg-emerald-500 text-center py-2.5 text-xs font-bold">Coming Soon</div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  </section>
);

/* ---------------------------------- FAQ ------------------------------------ */
const FAQ = () => {
  const faqs = [
    ['How long does setup take?', 'Once you request access, our team configures and onboards your organisation within 24 hours.'],
    ['Is my data safe and private?', 'Yes. Every organisation\'s data is fully isolated — your guests, properties and finances are never visible to anyone else.'],
    ['Can I manage multiple properties?', 'Absolutely. You can manage multiple properties, floors, rooms and beds with flexible sharing configurations from one dashboard.'],
    ['Do calculations handle notice & overstay?', 'Yes — rent is pro-rated for room shifts, and notice periods and overstays are tracked and calculated automatically.'],
    ['Can my staff have limited access?', 'Yes. Role-based access lets you give Admin, Manager, Accountant and Viewer permissions to your team.'],
    ['Can I export my reports?', 'All reports — Daily Hisab, Outstanding Dues, Ledger and guest lists — can be exported to Excel in one click.'],
  ];
  return (
    <section id="faq" className="py-28 px-6 md:px-10 bg-white">
      <div className="max-w-3xl mx-auto">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="text-center mb-12">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#002FA7] mb-3">FAQ</p>
          <h2 className={`${HEAD} text-4xl sm:text-5xl font-bold tracking-tight text-zinc-950`}>Questions, answered</h2>
        </motion.div>
        <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="rounded-xl border border-zinc-200 bg-[#FAFAFA] px-5">
              <AccordionTrigger className="text-left text-base font-semibold text-zinc-900 hover:no-underline py-5">{f[0]}</AccordionTrigger>
              <AccordionContent className="text-zinc-600 leading-relaxed pb-5">{f[1]}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

/* ------------------------------ Contact form ------------------------------- */
const ContactForm = () => {
  const [form, setForm] = useState({ name: '', org_name: '', phone: '', email: '', city: '', message: '' });
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [errMsg, setErrMsg] = useState('');

  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus('loading'); setErrMsg('');
    try {
      await axios.post(`${API}/api/enquiries`, form);
      setStatus('success');
      setForm({ name: '', org_name: '', phone: '', email: '', city: '', message: '' });
    } catch (err) {
      setStatus('error');
      setErrMsg(err?.response?.data?.detail || 'Something went wrong. Please try again or call us.');
    }
  };

  return (
    <section id="contact" className="py-28 px-6 md:px-10 bg-[#FAFAFA]">
      <div className="max-w-6xl mx-auto grid lg:grid-cols-[1fr_1.1fr] gap-12 items-start">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#002FA7] mb-3">Get in touch</p>
          <h2 className={`${HEAD} text-4xl sm:text-5xl font-bold tracking-tight text-zinc-950 mb-5`}>Book a free demo</h2>
          <p className="text-lg text-zinc-600 mb-10 max-w-md">See StayPoint in action. Tell us about your PG and our team will reach out to set you up.</p>
          <div className="space-y-5">
            {[[Phone, 'Phone', '+91 9904048600', 'tel:+919904048600'], [Mail, 'Email', 'info@staypointindia.com', 'mailto:info@staypointindia.com'], [MapPin, 'Location', 'India', null]].map(([Icon, label, val, href]) => (
              <div key={label} className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-white border border-zinc-200 flex items-center justify-center shadow-sm">
                  <Icon className="w-5 h-5 text-[#002FA7]" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-zinc-400 font-semibold">{label}</p>
                  {href ? <a href={href} className="text-zinc-900 font-semibold hover:text-[#002FA7]">{val}</a> : <p className="text-zinc-900 font-semibold">{val}</p>}
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={1}
          className="rounded-3xl bg-white border border-zinc-200 shadow-[0_30px_70px_-30px_rgba(0,47,167,0.3)] p-8 sm:p-10">
          {status === 'success' ? (
            <div data-testid="contact-success" className="text-center py-12">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-5">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className={`${HEAD} text-2xl font-bold text-zinc-950 mb-2`}>Thank you!</h3>
              <p className="text-zinc-600 mb-6">Our team will get in touch with you shortly.</p>
              <Button variant="outline" data-testid="contact-reset-btn" onClick={() => setStatus('idle')} className="rounded-xl">Send another enquiry</Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-zinc-700 mb-1.5 block">Your Name *</label>
                  <Input required data-testid="contact-name" value={form.name} onChange={update('name')} placeholder="Full name" className="rounded-xl h-11" />
                </div>
                <div>
                  <label className="text-sm font-medium text-zinc-700 mb-1.5 block">PG / Organisation</label>
                  <Input data-testid="contact-org" value={form.org_name} onChange={update('org_name')} placeholder="e.g. Stay Point Private Limited" className="rounded-xl h-11" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-zinc-700 mb-1.5 block">Phone *</label>
                  <Input required data-testid="contact-phone" value={form.phone} onChange={update('phone')} placeholder="10-digit mobile" className="rounded-xl h-11" />
                </div>
                <div>
                  <label className="text-sm font-medium text-zinc-700 mb-1.5 block">Email *</label>
                  <Input required type="email" data-testid="contact-email" value={form.email} onChange={update('email')} placeholder="you@example.com" className="rounded-xl h-11" />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-zinc-700 mb-1.5 block">City</label>
                <Input data-testid="contact-city" value={form.city} onChange={update('city')} placeholder="City" className="rounded-xl h-11" />
              </div>
              <div>
                <label className="text-sm font-medium text-zinc-700 mb-1.5 block">Message</label>
                <Textarea data-testid="contact-message" value={form.message} onChange={update('message')} placeholder="Tell us about your PG / requirement" className="rounded-xl min-h-[100px]" />
              </div>
              {status === 'error' && <p data-testid="contact-error" className="text-sm text-rose-600">{errMsg}</p>}
              <Button type="submit" data-testid="contact-submit" disabled={status === 'loading'}
                className="w-full bg-[#002FA7] hover:bg-[#00227A] text-white rounded-xl h-12 text-base font-semibold shadow-[0_12px_28px_-8px_rgba(0,47,167,0.5)]">
                {status === 'loading' ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</> : <>Request a Demo <ArrowRight className="ml-2 h-4 w-4" /></>}
              </Button>
              <p className="text-xs text-zinc-400 text-center">We'll never share your details. By submitting you agree to be contacted.</p>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};

/* --------------------------------- Footer ---------------------------------- */
const Footer = () => (
  <footer className="relative bg-zinc-950 text-white pt-20 pb-10 px-6 md:px-10 overflow-hidden">
    <p className={`${HEAD} pointer-events-none select-none absolute -bottom-6 left-0 right-0 text-center text-[18vw] font-black text-white/[0.03] leading-none`}>STAYPOINT</p>
    <div className="relative max-w-7xl mx-auto">
      <div className="grid md:grid-cols-[1.4fr_1fr_1fr] gap-12 pb-12 border-b border-white/10">
        <div>
          <div className="flex items-center gap-3 mb-5">
            <img src={LOGO_URL} alt="StayPoint" className="w-11 h-11 rounded-xl object-contain bg-white p-0.5" />
            <div>
              <h3 className={`${HEAD} font-extrabold text-lg`}>StayPoint</h3>
              <p className="text-xs text-zinc-400">Stay Point Private Limited</p>
            </div>
          </div>
          <p className="text-zinc-400 max-w-xs leading-relaxed">The easiest way to manage your PGs & hostels — guests, rent, electricity and accounting in one place.</p>
        </div>
        <div>
          <h4 className="font-semibold mb-4 text-sm uppercase tracking-wide text-zinc-300">Product</h4>
          <ul className="space-y-3 text-zinc-400">
            <li><a href="#features" className="hover:text-white">Features</a></li>
            <li><a href="#why-us" className="hover:text-white">Why StayPoint</a></li>
            <li><a href="#faq" className="hover:text-white">FAQ</a></li>
            <li><Link to="/login" className="hover:text-white">Login</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="font-semibold mb-4 text-sm uppercase tracking-wide text-zinc-300">Contact</h4>
          <ul className="space-y-3 text-zinc-400">
            <li className="flex items-center gap-2"><Phone className="w-4 h-4" /> <a href="tel:+919904048600" className="hover:text-white">+91 9904048600</a></li>
            <li className="flex items-center gap-2"><Mail className="w-4 h-4" /> <a href="mailto:info@staypointindia.com" className="hover:text-white">info@staypointindia.com</a></li>
            <li className="flex items-center gap-2"><MapPin className="w-4 h-4" /> India</li>
          </ul>
        </div>
      </div>
      <div className="pt-8 flex flex-col md:flex-row items-center justify-between gap-3 text-sm text-zinc-500">
        <p>© {new Date().getFullYear()} Stay Point Private Limited. All rights reserved.</p>
        <div className="flex items-center gap-4">
          <Link to="/privacy" className="hover:text-white">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-white">Terms of Service</Link>
        </div>
      </div>
    </div>
  </footer>
);

/* --------------------------------- Page ------------------------------------ */
export const LandingPage = () => (
  <div className="min-h-screen bg-white text-zinc-900 font-['Outfit',_'IBM_Plex_Sans',_sans-serif] antialiased" data-testid="landing-page">
    <Header />
    <Hero />
    <StatsStrip />
    <FeatureShowcase />
    <Comparison />
    <HowItWorks />
    <Testimonials />
    <AppSoon />
    <FAQ />
    <ContactForm />
    <Footer />
  </div>
);

export default LandingPage;
